import React, {useRef} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {Button, List} from '@tickchak/react-share/app';
import useShow from '../../../../../hooks/useShow';
import {getMenuPosition} from '../../../utils';
import {TTimetableItem} from '../../../../../redux/timetable/timetable.type';
import {deleteBreak} from '../../../../../redux/timetable/timetableActions';
import {bounceMessage} from '../../../../../layout/userMessages/UserMessage';
import KebabDots from '../../../../../assets/icons/KebabDots';
import XIcon from '../../../../../assets/icons/XIcon';
import EditIcon from '../../../../../assets/icons/EditIcon';
import TrashIcon from '../../../../../assets/icons/TrashIcon';

const TriggerButton = styled(Button)`
    &:focus, &:focus-visible { outline: none; box-shadow: none; }
`;

interface BreakCardMenuProps {
    breakItem: TTimetableItem;
    onEdit?: () => void;
}

const DROPDOWN_WIDTH = 240;

function BreakCardMenu({breakItem, onEdit = () => {}}: BreakCardMenuProps) {
    const {t} = useTranslation('timetable');
    const show = useShow();
    const triggerRef = useRef<HTMLSpanElement>(null);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        show.isOpen ? show.close() : show.open();
    };

    const performDelete = async () => {
        const ok = await deleteBreak(breakItem.ttid);
        if(!ok) bounceMessage(t('deleteError'), 'error');
    };

    const handleItemClick = ({id}: {id: string | number}) => {
        show.close();
        if(id === 'edit') { onEdit(); return; }
        if(id === 'delete') {
            bounceMessage(
                t('confirmDeleteBreakMessage'),
                'attention',
                {text: t('deleteBreak'), onClick: performDelete},
                {text: t('cancel')}
            );
        }
    };

    return (
        <>
            <span ref={triggerRef}>
                <TriggerButton
                    variant="subtle"
                    size="chip"
                    iconStart={<KebabDots />}
                    ariaLabel={t('breakActions')}
                    ariaHaspopup="menu"
                    ariaExpanded={show.isOpen}
                    onClick={toggle}
                    customStyle={{width: 14, height: 14, padding: 0, minWidth: 0, opacity: 0.6}}
                />
            </span>
            {show.isOpen && (
                <List
                    fixedPosition={getMenuPosition(triggerRef)}
                    show={show}
                    onItemClick={handleItemClick}
                    position={{width: `${DROPDOWN_WIDTH}px`, height: 'fit-content', minHeight: 'fit-content'}}
                >
                    <List.Items>
                        <List.Item.Item id="edit"   text={t('editBreak')}   endIcon={<EditIcon />} />
                        <List.Item.Item id="delete" text={t('deleteBreak')} endIcon={<TrashIcon />} />
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('close')} iconStart={<XIcon />} size="category" />
                    </List.Footer>
                </List>
            )}
        </>
    );
}

export default BreakCardMenu;
